import { Injectable } from "@angular/core";
import { CoreCOMService } from "./com.service";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { Coordinates } from "./com/comEnvironmentGeolocationCoordinates";
import { LocationServiceStatus } from "../shared/contentTypes";

/** Single access point for device location and postal-code lookup. */
@Injectable({
  providedIn: "root"
})
export class DeviceLocationService {

  constructor(
    private _coreComService: CoreCOMService,
    private _coreLogService: CoreLogService,
    private _circularInjectionHelper: CircularInjectionHelper) {
  }

  /** Returns the device's current coordinates; never rejects — resolves `null` if unavailable. */
  public async getCurrentPosition(): Promise<Coordinates> {
    const me = this;
    try {
      if (me._coreComService.environment.client.isMobile) {
        const isLocationEnabled = await me._coreComService.environment.Diagnostic.isLocationEnabled();
        if (isLocationEnabled) {
          const coordinates = await me._coreComService.environment.geolocation.currentPosition(true, 5000, 10000);
          return coordinates;
        } else {
          me._coreLogService.info("DeviceLocationService.getCurrentPosition", "currentPosition", "locationService is off");
          return null;
        }
      } else {
        const coordinates = await me._coreComService.environment.geolocation.currentPosition(true, 5000, 10000);
        return coordinates;
      }
    } catch (e) {
      me._coreLogService.error("DeviceLocationService.getCurrentPosition", "currentPosition try catch", e);
      return null;
    }
  }

  /** Returns the location-service status (`BrowserMode` outside the native app);
   * never rejects — resolves `ResponseError` on failure. */
  public async getLocationServiceStatus(): Promise<LocationServiceStatus> {
    const me = this;
    try {
      if (me._coreComService.environment.client.isMobile) {
        const isLocationEnabled = await me._coreComService.environment.Diagnostic.isLocationEnabled();
        if (isLocationEnabled) {
          const isLocationServiceAuthorized = await me._coreComService.environment.Diagnostic.isLocationAuthorized();
          if (isLocationServiceAuthorized) {
            return LocationServiceStatus.Authorized;
          } else {
            return LocationServiceStatus.NotAuthorized;
          }
        } else {
          return LocationServiceStatus.IsNotActivated;
        }
      } else {
        return LocationServiceStatus.BrowserMode;
      }
    } catch (error) {
      me._coreLogService.error("DeviceLocationService.getLocationServiceStatus", "try catch", error);
      return LocationServiceStatus.ResponseError;
    }
  }

  /** Returns the postal code for the coordinates; never rejects — resolves `""` if unavailable. */
  public async getPostalCodeForCoordinates(coordinates: Coordinates): Promise<string> {
    const me = this;
    try {
      if (coordinates && coordinates.success) {
        const postalCode = await me._circularInjectionHelper.yourAppServer.Api.GetZipFromGeolocation(`${coordinates.latitude},${coordinates.longitude}`);
        return postalCode;
      } else {
        return "";
      }
    } catch (e) {
      me._coreLogService.error("DeviceLocationService.getPostalCodeForCoordinates", "GetZipFromGeolocation", e);
      return "";
    }
  }
}
